<script setup>
import { useStoryblokStore } from '@/stores/storyblok'
import { computed, nextTick, useTemplateRef, watch } from 'vue'

const storyblokStore = useStoryblokStore()
const logContainer = useTemplateRef('logContainer')

const status = computed(() => {
  if (storyblokStore.progress < 100) return 'progress'
  else if (storyblokStore.error) return 'error'
  return 'success'
})

const theme = computed(() => {
  return storyblokStore.pagesCreated.find((page) => page.slug === '_theme')
})

async function scrollToBottom() {
  await nextTick()
  const el = logContainer.value
  if (el) el.scrollTop = el.scrollHeight
}

function formatDate(timestampStr) {
  const d = new Date(timestampStr)
  // To add leading zeros if needed
  const pad = (n, z = 2) => String(n).padStart(z, '0')

  // `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
  return (
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.` +
    `${pad(d.getMilliseconds(), 3)}`
  )
}

watch(
  () => storyblokStore.generationLogs.length,
  () => {
    scrollToBottom()
  },
)
</script>
<template>
  <div class="">
    <div class="flex gap-12" :class="{ 'items-center': status !== 'progress' }">
      <p class="mt-6" v-if="status === 'progress'">
        We're setting up the site in Storyblok. This process may take several minutes. Please, don't
        close this tab.
      </p>
      <div v-else-if="status === 'success'">
        <h2 class="text-3xl mb-2">Setup complete!</h2>
        <p class="mb-2">
          Site <span class="font-bold">{{ storyblokStore.siteSlug }}</span> has been successfully
          created.
        </p>
        <a
          class="underline text-primary-600"
          target="_blank"
          :href="`https://app.storyblok.com/#/me/spaces/${storyblokStore.space.id}/stories/0/0/index/${storyblokStore.folderCreated.id}`"
          >Go to folder on Storyblok</a
        >
      </div>
      <div v-else-if="status === 'error'">
        <h2 class="text-3xl mb-2">Setup failed</h2>
        <p class="mb-2">
          We couldn't create the site
          <span class="font-bold">{{ storyblokStore.siteSlug }}</span> in the space
          {{ storyblokStore.space.name }}. Check the logs below for more details.
        </p>
        <div class="flex gap-4 mt-4">
          <UButton
            label="Retry"
            icon="i-lucide-refresh-cw"
            @click="storyblokStore.generateSite()"
          ></UButton>
          <UButton
            label="Restart configuration"
            icon="i-lucide-undo-2"
            variant="outline"
            @click="storyblokStore.restart()"
          ></UButton>
        </div>
      </div>

      <GenerationAnimation :status="status"></GenerationAnimation>
    </div>
    <div class="mt-8" v-if="status !== 'success'">
      <ol
        ref="logContainer"
        class="inset-shadow-sm text-sm w-full p-4 overflow-y-auto rounded-t-md h-52 text-gray-500 border-gray-200 border light-scrollbar"
      >
        <li
          v-for="(log, index) in storyblokStore.generationLogs"
          :key="index"
          class="grid grid-cols-[110px_1fr]"
        >
          <span>[{{ formatDate(log.timestamp) }}]</span> <span>{{ log.message }}</span>
        </li>
      </ol>
      <UProgress
        v-model="storyblokStore.progress"
        color="secondary"
        :ui="{ base: 'rounded-none rounded-b-md' }"
      />
    </div>
    <div v-else class="mt-8">
      <div class="bg-info-50 rounded-lg px-6 pt-4 pb-6">
        <h3 class="text-2xl">Next steps</h3>
        <ol class="flex flex-col gap-3 mt-4">
          <li class="flex gap-3 items-center">
            <span
              class="shrink-0 w-6 h-6 bg-info-500 text-white rounded-full flex items-center justify-center text-xs font-semibold"
              >1</span
            >
            <div>
              Change colors and typography in the
              <a
                :href="`https://app.storyblok.com/#/me/spaces/${storyblokStore.space.id}/stories/0/0/${theme?.id}`"
                target="_blank"
                class="underline text-primary-600"
                >theme</a
              >
            </div>
          </li>
          <li class="flex gap-3 items-center">
            <span
              class="shrink-0 w-6 h-6 bg-info-500 text-white rounded-full flex items-center justify-center text-xs font-semibold"
              >2</span
            >
            Review and change text & images in all pages
          </li>
          <li class="flex gap-3 items-center">
            <span
              class="shrink-0 w-6 h-6 bg-info-500 text-white rounded-full flex items-center justify-center text-xs font-semibold"
              >3</span
            >
            Publish all pages
          </li>
        </ol>
      </div>

      <UCollapsible class="flex mt-6 flex-col gap-2 w-full border-primary-200 border-2 rounded-lg">
        <UButton
          class="group bg-white"
          :label="`Pages created (${storyblokStore.pagesCreated.length})`"
          color="neutral"
          variant="soft"
          size="xl"
          icon="i-lucide-files"
          trailing-icon="i-lucide-chevron-down"
          :ui="{
            trailingIcon: 'group-data-[state=open]:rotate-180 transition-transform duration-200',
          }"
          block
        />
        <template #content>
          <ul class="px-6 mb-4 mt-2 gap-y-2 gap-x-12 grid grid-cols-2">
            <li
              v-for="page in storyblokStore.pagesCreated"
              :key="page.id"
              class="flex justify-between"
            >
              <UButton
                class="w-full"
                :label="page.name"
                color="neutral"
                variant="soft"
                size="md"
                trailing-icon="i-lucide-external-link"
                :to="`https://app.storyblok.com/#/me/spaces/${storyblokStore.space.id}/stories/0/0/${page.id}`"
                target="_blank"
                :ui="{ label: 'w-full', trailingIcon: 'text-primary-600' }"
              ></UButton>
            </li>
          </ul>
        </template>
      </UCollapsible>
      <p class="text-xs text-gray-500 text-center mt-6 -mb-8">(you can close this tab now)</p>
    </div>
  </div>
</template>
